import { useEffect, useState, type FormEvent } from "react";
import { createClient } from "@supabase/supabase-js";
import { supabase } from "./supabase.js";
import { Button, Card, Input } from "./ui.js";

interface TotpFactor {
  id: string;
  status: "verified" | "unverified";
  friendly_name?: string;
}

/** Checks a password without touching the signed-in session: a throwaway client that never stores anything. */
async function passwordIsCorrect(email: string, password: string): Promise<boolean> {
  const probe = createClient(import.meta.env.VITE_SUPABASE_URL ?? "", import.meta.env.VITE_SUPABASE_ANON_KEY ?? "", {
    auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
  });
  const { error } = await probe.auth.signInWithPassword({ email, password });
  return !error;
}

/** Change password: asks for the current one first, then sets the new one on the signed-in account. */
function ChangePassword() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setDone(false);
    if (next.length < 8) return setError("Use at least 8 characters for the new password.");
    if (next !== confirm) return setError("The new passwords don't match.");
    if (next === current) return setError("The new password must be different from the current one.");
    setBusy(true);
    const { data } = await supabase.auth.getUser();
    const email = data.user?.email;
    if (!email || !(await passwordIsCorrect(email, current))) {
      setBusy(false);
      return setError("The current password isn't right.");
    }
    const { error: err } = await supabase.auth.updateUser({ password: next });
    setBusy(false);
    if (err) return setError(err.message);
    setCurrent("");
    setNext("");
    setConfirm("");
    setDone(true);
  }

  const field: React.CSSProperties = { display: "block", marginTop: 4 };
  return (
    <form onSubmit={handleSubmit} style={{ fontSize: 14 }}>
      <h2 className="rc-h2" style={{ marginBottom: 4 }}>Change password</h2>
      <p style={{ color: "var(--rc-text-muted)", margin: "0 0 16px" }}>Use at least 8 characters. You will stay signed in on this device.</p>
      <label style={{ display: "block", marginBottom: 12, fontSize: 13, fontWeight: 600, color: "var(--rc-text-muted)" }}>
        Current password
        <Input type="password" required autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} style={field} />
      </label>
      <label style={{ display: "block", marginBottom: 12, fontSize: 13, fontWeight: 600, color: "var(--rc-text-muted)" }}>
        New password
        <Input type="password" required minLength={8} autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} style={field} />
      </label>
      <label style={{ display: "block", marginBottom: 16, fontSize: 13, fontWeight: 600, color: "var(--rc-text-muted)" }}>
        Confirm new password
        <Input type="password" required autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} style={field} />
      </label>
      {error && <p role="alert" style={{ color: "var(--rc-danger)", marginTop: 0 }}>{error}</p>}
      {done && <p role="status" style={{ color: "var(--rc-success)", marginTop: 0, fontWeight: 600 }}>Password changed.</p>}
      <Button variant="primary" type="submit" disabled={busy}>
        {busy ? "Changing..." : "Change password"}
      </Button>
    </form>
  );
}

/**
 * Self-service TOTP enrollment, shared by /app/security and /admin/security -- same
 * account, same supabase.auth.mfa API regardless of which portal you're in. Enrollment is
 * optional (docs/architecture.md section 8): nothing here is forced, but once a factor is
 * verified, Login.tsx's code-entry step becomes mandatory on every future sign-in for this
 * account (Guards.tsx / lib/auth.tsx mfaPending).
 */
export function Security() {
  const [factors, setFactors] = useState<TotpFactor[]>([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState<{ factorId: string; qrSvg: string; secret: string } | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    const { data } = await supabase.auth.mfa.listFactors();
    setFactors((data?.totp ?? []) as TotpFactor[]);
    setLoading(false);
  }

  useEffect(() => {
    void load();
  }, []);

  const verifiedFactor = factors.find((f) => f.status === "verified");

  async function handleEnroll() {
    setError(null);
    setBusy(true);
    const { data, error: err } = await supabase.auth.mfa.enroll({ factorType: "totp" });
    setBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    setEnrolling({ factorId: data.id, qrSvg: data.totp.qr_code, secret: data.totp.secret });
  }

  async function handleConfirm(e: FormEvent) {
    e.preventDefault();
    if (!enrolling) return;
    setError(null);
    setBusy(true);
    const { data: challenge, error: challengeErr } = await supabase.auth.mfa.challenge({ factorId: enrolling.factorId });
    if (challengeErr) {
      setBusy(false);
      setError(challengeErr.message);
      return;
    }
    const { error: verifyErr } = await supabase.auth.mfa.verify({ factorId: enrolling.factorId, challengeId: challenge.id, code });
    setBusy(false);
    if (verifyErr) {
      setError(verifyErr.message);
      return;
    }
    setEnrolling(null);
    setCode("");
    await load();
  }

  async function handleCancelEnroll() {
    if (!enrolling) return;
    await supabase.auth.mfa.unenroll({ factorId: enrolling.factorId });
    setEnrolling(null);
    setCode("");
  }

  async function handleRemove(factorId: string) {
    setError(null);
    setBusy(true);
    const { error: err } = await supabase.auth.mfa.unenroll({ factorId });
    setBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    await load();
  }

  if (loading) return <p className="rc-loading">Loading...</p>;

  return (
    <div>
      <h1 className="rc-h1" style={{ marginBottom: 16 }}>Security</h1>
      <Card style={{ marginBottom: 20 }}>
        <ChangePassword />
      </Card>
      <Card>
      <h2 className="rc-h2" style={{ marginBottom: 4 }}>Two-factor authentication</h2>

      {error && <p role="alert" style={{ color: "var(--rc-danger)" }}>{error}</p>}

      {verifiedFactor ? (
        <div style={{ fontSize: 14 }}>
          <p style={{ color: "var(--rc-success)", fontWeight: 600, marginTop: 4 }}>Enabled. You'll be asked for a code from your authenticator app on every sign-in.</p>
          <Button onClick={() => void handleRemove(verifiedFactor.id)} disabled={busy}>
            Turn off
          </Button>
        </div>
      ) : enrolling ? (
        <form onSubmit={handleConfirm} style={{ fontSize: 14 }}>
          <p>Scan this with Google Authenticator (or any TOTP app), then enter the 6-digit code it shows.</p>
          <div style={{ width: 180, height: 180, margin: "12px 0" }} dangerouslySetInnerHTML={{ __html: enrolling.qrSvg }} />
          <p style={{ color: "var(--rc-text-muted)" }}>
            Can't scan it? Enter this key manually: <code>{enrolling.secret}</code>
          </p>
          <label style={{ display: "block", marginTop: 12, marginBottom: 12, fontSize: 13, fontWeight: 600, color: "var(--rc-text-muted)" }}>
            Code
            <Input
              inputMode="numeric"
              pattern="[0-9]*"
              autoFocus
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              style={{ display: "block", marginTop: 4, letterSpacing: 2, width: 200 }}
            />
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="primary" type="submit" disabled={busy}>
              Confirm
            </Button>
            <Button type="button" onClick={() => void handleCancelEnroll()} disabled={busy}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <div style={{ fontSize: 14 }}>
          <p style={{ color: "var(--rc-text-muted)", marginTop: 4 }}>Not set up. Sign-in only needs your password.</p>
          <Button variant="primary" onClick={() => void handleEnroll()} disabled={busy}>
            Enable two-factor authentication
          </Button>
        </div>
      )}
      </Card>
    </div>
  );
}
